'use client'

import { useEffect } from 'react'
import { useAuth } from '@clerk/nextjs'
import { cn } from '@/utils/styles/cn'
import { useSettingsModal, IOrganizationPage } from '@/features/settings-modal/settings-modal-context'
import { GeneralSection } from './sections/general-section'
import { MembersSection } from './sections/members-section'
import { CompanyDomainSection } from './sections/company-domain-section'
import { useGetCompanyDomainQuery } from '@/api/organization/company-domain/get-company-domain-query'
import { resolveCompanyDomainAccess } from './utils/company-domain-access'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'

const BASE_SUBTABS: { id: IOrganizationPage; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'members', label: 'Members' },
]

export const OrganizationTab = () => {
    const { actor, orgRole } = useAuth()
    const isSuperUser = !!useIsSuperUser()
    const { options, setOptions } = useSettingsModal()
    const activeSubtab = options.organizationPage || 'general'
    const companyDomainQuery = useGetCompanyDomainQuery()
    const companyDomainAccess = resolveCompanyDomainAccess({
        clientCanView: orgRole === 'org:admin' || isSuperUser || Boolean(actor),
        querySucceeded: companyDomainQuery.isSuccess || companyDomainQuery.data !== undefined,
        queryFetching: companyDomainQuery.isFetching,
        queryFailed: companyDomainQuery.isError,
        queryError: companyDomainQuery.error,
        canAttempt: companyDomainQuery.data?.add.canAttempt,
    })
    const subtabs = companyDomainAccess.showTab
        ? [...BASE_SUBTABS, { id: 'domain' as const, label: 'Company domain' }]
        : BASE_SUBTABS

    useEffect(() => {
        if (activeSubtab === 'domain' && companyDomainAccess.forbidden) {
            setOptions({ organizationPage: 'general' })
        }
    }, [activeSubtab, companyDomainAccess.forbidden, setOptions])

    return (
        <div className="flex flex-col gap-6">
            <div className="flex gap-1 border-b border-border">
                {subtabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setOptions({ organizationPage: tab.id })}
                        className={cn(
                            'px-3 py-2 text-sm font-medium transition-colors relative',
                            activeSubtab === tab.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {tab.label}
                        {activeSubtab === tab.id && (
                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
                        )}
                    </button>
                ))}
            </div>

            {activeSubtab === 'general' && <GeneralSection />}
            {activeSubtab === 'members' && <MembersSection />}
            {activeSubtab === 'domain' && companyDomainAccess.showTab && (
                <>
                    {companyDomainAccess.showRetry && (
                        <div
                            className="flex items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/5 px-3 py-2.5"
                            role="alert"
                        >
                            <div className="flex min-w-0 items-start gap-2">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                                <p className="text-sm text-destructive">
                                    {companyDomainQuery.data
                                        ? "We couldn't refresh the company domain status. Your current verification state was kept."
                                        : "We couldn't load the company domain status. Try again."}
                                </p>
                            </div>
                            <Button
                                variant="ds2-secondary"
                                size="ds2"
                                className="shrink-0"
                                onClick={() => void companyDomainQuery.refetch()}
                                disabled={companyDomainQuery.isFetching}
                                isLoading={companyDomainQuery.isFetching}
                            >
                                Retry
                            </Button>
                        </div>
                    )}
                    {!companyDomainQuery.data && !companyDomainAccess.showRetry && (
                        <div className="space-y-3" role="status" aria-label="Loading company domain status">
                            <Skeleton className="h-5 w-36" />
                            <Skeleton className="h-28 rounded-xl" />
                        </div>
                    )}
                    {companyDomainQuery.data && (
                        <CompanyDomainSection
                            companyDomain={companyDomainQuery.data}
                            refreshCompanyDomain={companyDomainQuery.refetch}
                            canManage={companyDomainAccess.canManage}
                        />
                    )}
                </>
            )}
        </div>
    )
}
